// DS2 Table: a data table (even rows tinted; emptyState under it; dense: cells 6 10, 13px, for numeric tables), its
// cells (TableHeadCell left-aligned by default, TableCell with noWrap, the narrow TableIconCell and TableActionsCell),
// TableRow with a delayed onRowClick, Pagination (from - to of total, previous / next) and KebabButton, the row
// actions' button.
import cx from "classnames";
import {
  useEffect,
  useRef,
  type ButtonHTMLAttributes,
  type CSSProperties,
  type HTMLAttributes,
  type MouseEvent,
  type PropsWithChildren,
  type ReactNode,
  type Ref,
  type TdHTMLAttributes,
  type ThHTMLAttributes,
} from "react";
import useI18n from "../../hooks/useTranslations";
import { ChevronDownIcon } from "./icons";
import styles from "./Table.module.css";

export type TableProps = HTMLAttributes<HTMLTableElement> & {
  dense?: boolean;
  wrapperClassName?: string;
  wrapperStyle?: CSSProperties;
  /** shown under the table (e.g. when there are no rows) */
  emptyState?: ReactNode;
  ref?: Ref<HTMLTableElement>;
};

export function Table({
  dense,
  wrapperClassName,
  wrapperStyle,
  emptyState,
  className,
  children,
  ...props
}: TableProps) {
  return (
    <div className={cx(styles.wrap, wrapperClassName)} style={wrapperStyle}>
      <table className={cx(styles.table, dense && styles.dense, className)} {...props}>
        {children}
      </table>
      {emptyState}
    </div>
  );
}

export type TableHeadCellProps = ThHTMLAttributes<HTMLTableCellElement>;

export function TableHeadCell({ align, className, ...props }: TableHeadCellProps) {
  return <th scope="col" align={align || "left"} className={cx(styles.headCell, className)} {...props} />;
}

export type TableRowProps = Omit<HTMLAttributes<HTMLTableRowElement>, "onClick" | "onDoubleClick"> & {
  onRowClick?: (event: MouseEvent<HTMLTableRowElement>) => void;
};

const ROW_CLICK_DELAY = 200;

// onRowClick runs 200 ms after the click, so a double click (e.g. to select a word) cancels it; a click that ends a
// text selection is ignored. data-hover-reveal: a hook for controls inside that show on the row's hover.
export function TableRow({ onRowClick, className, ...props }: TableRowProps) {
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  useEffect(() => () => clearTimeout(timer.current), []);
  return (
    <tr
      className={cx(onRowClick && styles.clickable, className)}
      data-hover-reveal
      onClick={
        onRowClick
          ? (event) => {
              if (window.getSelection()?.toString().length) return;
              clearTimeout(timer.current); // the double click's first click too, not only its second
              timer.current = setTimeout(() => onRowClick(event), ROW_CLICK_DELAY);
            }
          : undefined
      }
      onDoubleClick={onRowClick ? () => clearTimeout(timer.current) : undefined}
      {...props}
    />
  );
}

export type TableCellProps = TdHTMLAttributes<HTMLTableCellElement> & {
  noWrap?: boolean;
};

export function TableCell({ noWrap, className, ...props }: TableCellProps) {
  return <td className={cx(styles.cell, noWrap && styles.noWrap, className)} {...props} />;
}

// a narrow cell with a centred icon
export function TableIconCell({ children, className }: PropsWithChildren<{ className?: string }>) {
  return (
    <td className={cx(styles.cell, styles.iconCell, className)}>
      <div className={styles.iconCellInner}>{children}</div>
    </td>
  );
}

// a narrow cell for the row's actions, at the right
export function TableActionsCell({ children, className }: PropsWithChildren<{ className?: string }>) {
  return <td className={cx(styles.cell, styles.actions, className)}>{children}</td>;
}

export type PaginationProps = {
  from: number;
  to: number;
  total: number;
  onPrevious: () => void;
  onNext: () => void;
  canGoPrevious?: boolean;
  canGoNext?: boolean;
  ofLabel?: string;
  previousLabel?: string;
  nextLabel?: string;
  className?: string;
};

export function Pagination({
  from,
  to,
  total,
  onPrevious,
  onNext,
  canGoPrevious = true,
  canGoNext = true,
  ofLabel,
  previousLabel,
  nextLabel,
  className,
}: PaginationProps) {
  const t = useI18n().translation.designSystem;
  const range = (a: number, b: number) => `${a} - ${b} ${ofLabel ?? t.paginationOfLabel} ${total}`;
  return (
    <div className={cx(styles.pagination, className)}>
      {/* as wide as the longest range ("25 - 25 of 25", an invisible copy under it), so the arrows do not move */}
      <span className={styles.range}>
        <span className={styles.rangeWidest} aria-hidden="true">
          {range(total, total)}
        </span>
        <span>{range(from, to)}</span>
      </span>
      <button
        type="button"
        className={cx(styles.pageButton, styles.prev)}
        aria-label={previousLabel ?? t.previousPageLabel}
        disabled={!canGoPrevious}
        onClick={onPrevious}
      >
        <ChevronDownIcon />
      </button>
      <button
        type="button"
        className={cx(styles.pageButton, styles.next)}
        aria-label={nextLabel ?? t.nextPageLabel}
        disabled={!canGoNext}
        onClick={onNext}
      >
        <ChevronDownIcon />
      </button>
    </div>
  );
}

// the row actions' button (opens a Menu): a 26px circle with three dots
export function KebabButton({
  className,
  "aria-label": label,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement>) {
  const t = useI18n().translation.designSystem;
  return (
    <button
      type="button"
      className={cx(styles.kebab, className)}
      aria-label={label ?? t.contextMenuTriggerLabel}
      {...props}
    />
  );
}

export default Table;
